// material UI
import { Button, Grid, MenuItem, TextField } from '@material-ui/core';
import TimelineIcon from '@material-ui/icons/Timeline';
import { flatten, omit } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';

// api
import { LiveDataEventTypes, track } from '~/src/dash/analytics/analytics';
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import {
  IDimensions,
  IHeaderBidder,
  IItemMetrics,
  IPublisher,
  IRealtimeAnalytics,
  ISite,
  IUser,
} from '~/src/dash/api/console-api';
import { IDeal } from '~/src/dash/api/deals';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { queryRealtimeAnalytics } from '~/src/dash/api/realtime-analytics';
import { EBool } from '~/src/dash/api/types/bool';
import { formatTooltipDateInTimezone } from '~/src/dash/components/charts/common';
import {
  CurrentlyWithinDayparting,
  parseDayparting,
} from '~/src/dash/components/dayparting-selector';
// components
import { LiveDataMultiSelect } from '~/src/dash/components/dropdowns/live-data-multi-select';
import { LiveDataGraph } from '~/src/dash/components/live-data/live-data-graph';
import { LiveDataKPIs } from '~/src/dash/components/live-data/live-data-kpi';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import {
  DensityRange1HourByMinutes,
  DensityRange4HoursByHour,
  DensityRange4HoursByMinutes,
  DensityRange12HoursByHour,
  DensityRange15Minutes,
  DensityRange24HoursByHour,
  DensityRange30Minutes,
  DensityRangeWeekByHour,
} from '~/src/dash/globals/constants';
import { Col, Cols, ct, spacing, Text } from '~/src/dash/publicaUI';
// views
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { graphColors } from '~/src/dash/utils/colors';
// utils
import {
  formatUnixToTimeExtendedInTimezone,
  formatUnixToTimeInTimezone,
  formatUnixToTimeWithDateInTimezone,
} from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isSamsungPublisher } from '~/src/dash/utils/ui';
import { networkHomeLiveDataKeys } from '~/src/dash/views/networks/constants';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
export type LiveDataResourceType =
  | 'Stream'
  | 'StreamKPI'
  | 'StreamLatency'
  | 'StreamError'
  | 'Site'
  | 'Campaign'
  | 'Bidder'
  | 'Deal'
  | 'InventoryRoute'
  | 'AutomatedBidFloor';

export interface IProps
  extends IWithStylesProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  sites?: ISite[];
  headerbidders?: IHeaderBidder[];
  deals?: IDeal[];
  campaign?: IAdserverCampaign;
  resourceIDs: string; // streamID, siteID, campaignIDs, etc, can be comma separated no space '1,2,3,4' for orders
  resourceType: LiveDataResourceType; // stream, site, campaign, etc
  timezone: string;
  maxWidth: string;
  publisher: IPublisher;
  userInfo?: IUser;
  bidderViewTabIndex?: number;
}

interface IState extends IBaseState {
  loading1: LoadingState; // loading state for query
  data1: IRealtimeAnalytics[]; // data1 result of the real time query, combined for display (each index equal to the time period)
  data1raw: IRealtimeAnalytics[]; // data1 result of the real time query but still minute by minute
  metricsList: IMenuList; // metrics list for dropdown
  selectedMetrics: ISelectedMetrics; // which metrics are displayed by the graph => metric to color code map
  selectedIDs: string[]; // selected IDs for graph like inventory split
  densityRange: number; // data range for graph (15, 30, 60 min, 4 hr, 12 hr)
  hidden: boolean; // shows the Live Analytics button or the whole component
  liveDataKPIsDisplay: ILiveDataKPIsDisplay; // aggregated data for KPI display
  snackbarError: boolean;
  snackbarMessage: string;
  currencySymbol: string;
  allBidderABTestingConfigs: { [key: string]: IListItem };
  allBidderABTestingTests: { [key: string]: IListItem };
  currentBidderABTestingName: string;
  filterBidderABTestingName: boolean;
  allChannelABTestingConfigs: { [key: string]: IListItem };
  allChannelABTestingTests: { [key: string]: IListItem };
  currentChannelABTestingName: string;
  filterChannelABTestingName: boolean;
  stackedBarMetrics: { [key: string]: string[] };
  // for an optional filter on the graph
  includeFilter: boolean;
  filterTitle: string;
  doNotDisplayTitleOptions: string[];
  filterMenuTitle: string;
}

// add any additional metrics to be shown on graph here
// the string represents the color shown on the graph
export interface ISelectedMetrics {
  // site
  Revenue?: IMetricGraphInfo;
  Impressions?: IMetricGraphInfo;
  ImpressionsUnpaid?: IMetricGraphInfo;
  CPM?: IMetricGraphInfo;
  CPMUnpaid?: IMetricGraphInfo;
  CPMPerSecond?: IMetricGraphInfo;
  EndpointRequests?: IMetricGraphInfo;
  EventAuctionInits?: IMetricGraphInfo;
  BidResponses?: IMetricGraphInfo;
  BidRequests?: IMetricGraphInfo;
  // stream
  AvgStreamAdFilledLatency?: IMetricGraphInfo;
  StreamAdFilled?: IMetricGraphInfo;
  StreamPodRequest?: IMetricGraphInfo;
  StreamPodFilled?: IMetricGraphInfo;
  StreamAdImpression?: IMetricGraphInfo;
  StreamAdComplete?: IMetricGraphInfo;
  StreamAdFetchTimeout?: IMetricGraphInfo;
  StreamChunklistFetch?: IMetricGraphInfo;
  StreamChunklistResponse?: IMetricGraphInfo;
  StreamError?: IMetricGraphInfo;
  StreamPlaylistTimeout?: IMetricGraphInfo;
  StreamStart?: IMetricGraphInfo;
  StreamAdChunklistFetch?: IMetricGraphInfo;
  StreamAdChunklistResponse?: IMetricGraphInfo;
  StreamAdFilledLatency?: IMetricGraphInfo;
  StreamFetchTimeout?: IMetricGraphInfo;
  StreamChunklistTimeout?: IMetricGraphInfo;
  StreamTranscodeMiss?: IMetricGraphInfo;
  StreamTranscodeMissError?: IMetricGraphInfo;
  StreamCachedAdMiss?: IMetricGraphInfo;
  StreamAdsFetchError?: IMetricGraphInfo;
  StreamPlaylistError?: IMetricGraphInfo;
  StreamChunklistError?: IMetricGraphInfo;
  StreamCacheReadMissError?: IMetricGraphInfo;
  // campaign
  AdserverRequest?: IMetricGraphInfo;
  AdserverInventoryRequest?: IMetricGraphInfo;
  AdserverFilled?: IMetricGraphInfo;
  AdserverImpression?: IMetricGraphInfo;
  AdserverComplete?: IMetricGraphInfo;
  AdserverImpressionTest?: IMetricGraphInfo;
  AdserverPacedRequestTest?: IMetricGraphInfo;
  // bidder
  WinningBids?: IMetricGraphInfo;
  // inventory routing
  DestinationErrors?: IMetricGraphInfo;
  DestinationLatency?: IMetricGraphInfo;
  DestinationNoFills?: IMetricGraphInfo;
  DestinationRequests?: IMetricGraphInfo;
  DestinationResponses?: IMetricGraphInfo;
  DestinationTimeouts?: IMetricGraphInfo;
  DestinationWins?: IMetricGraphInfo;
  InventoryRouteEndpointLatency?: IMetricGraphInfo;
  InventoryRouteErrors?: IMetricGraphInfo;
  InventoryRouteNoFills?: IMetricGraphInfo;
  InventoryRouteRequests?: IMetricGraphInfo;
  InventoryRouteResponses?: IMetricGraphInfo;
  // automated bid floor
  EstimatedRevenueOptimized?: IMetricGraphInfo;
  EstimatedRevenueLow?: IMetricGraphInfo;
  EstimatedRevenueHigh?: IMetricGraphInfo;
  BidFloorOptimized?: IMetricGraphInfo;
  BidFloorLow?: IMetricGraphInfo;
  BidFloorHigh?: IMetricGraphInfo;
  CPMOptimized?: IMetricGraphInfo;
  CPMLow?: IMetricGraphInfo;
  CPMHigh?: IMetricGraphInfo;
  BidRequestsOptimized?: IMetricGraphInfo;
  BidRequestsLow?: IMetricGraphInfo;
  BidRequestsHigh?: IMetricGraphInfo;
  BidResponsesOptimized?: IMetricGraphInfo;
  BidResponsesLow?: IMetricGraphInfo;
  BidResponsesHigh?: IMetricGraphInfo;
  WinningBidsOptimized?: IMetricGraphInfo;
  WinningBidsLow?: IMetricGraphInfo;
  WinningBidsHigh?: IMetricGraphInfo;
}

// graph info needed to create the Lines/Bars
export interface IMetricGraphInfo {
  color: string;
  graphType: string;
  yAxisId: string;
}

// aerospike metric keys to be iterated over when querying the data
export type AerospikeMetricKeys =
  // site
  | 'endpoint_request'
  | 'impressions'
  | 'auction_init'
  | 'cpm'
  | 'bid_response'
  | 'bid_request'
  | 'cpm_unpaid'
  | 'impressions_unpaid'
  // stream
  | 'stream_chunklist_response' // also streamKPI
  | 'stream_start' // also streamKPI
  | 'stream_pod_request' // also streamKPI
  | 'stream_ad_filled' // also streamKPI
  | 'stream_ad_impression' // also streamKPI
  | 'stream_ad_complete' // also streamKPI
  | 'stream_transcode_miss'
  // streamKPI
  | 'stream_pod_filled'
  | 'stream_chunklist_fetch'
  // streamLatency
  | 'stream_ad_filled_latency'
  | 'stream_ad_fetch_timeout'
  | 'stream_playlist_timeout'
  | 'stream_chunklist_timeout'
  // streamError
  | 'stream_transcode_miss_error'
  | 'stream_cached_ad_miss'
  | 'stream_ads_fetch_error'
  | 'stream_playlist_error'
  | 'stream_chunklist_error'
  | 'stream_cache_read_miss_error'
  // campaign
  | 'adserver_request'
  | 'adserver_filled'
  | 'adserver_impression'
  | 'adserver_complete'
  | 'adserver_impression_test'
  | 'adserver_paced_request_test'
  // bidder
  | 'bid_won'
  // inventory routing
  | 'inventory_route_endpoint_request'
  | 'inventory_route_endpoint_response'
  | 'inventory_route_endpoint_request_error'
  | 'inventory_route_endpoint_request_no_fill'
  | 'inventory_route_destination_request'
  | 'inventory_route_destination_response'
  | 'inventory_route_destination_timeout'
  | 'inventory_route_destination_no_fill'
  | 'inventory_route_destination_request_error'
  | 'inventory_route_destination_win'
  // automated bid floor
  | 'bid_floor'
  | 'bid_won_cpm';

// graph metric keys
export type GraphMetricKeys =
  // site
  | 'EndpointRequests'
  | 'Impressions'
  | 'EventAuctionInits'
  | 'CPM'
  | 'CPMPerSecond'
  | 'Revenue'
  | 'BidResponses'
  | 'BidRequests'
  | 'ImpressionsUnpaid'
  | 'CPMUnpaid'
  // stream
  | 'StreamAdFilled'
  | 'StreamPodRequest'
  | 'StreamPodFilled'
  | 'StreamAdImpression'
  | 'AvgStreamAdFilledLatency'
  | 'StreamAdComplete'
  | 'StreamAdFetchTimeout'
  | 'StreamChunklistFetch'
  | 'StreamChunklistResponse'
  | 'StreamError'
  | 'StreamPlaylistTimeout'
  | 'StreamStart'
  | 'StreamAdChunklistFetch'
  | 'StreamAdChunklistResponse'
  | 'StreamAdFilledLatency'
  | 'StreamFetchTimeout'
  | 'StreamChunklistTimeout'
  | 'StreamTranscodeMiss'
  | 'StreamTranscodeMissError'
  | 'StreamCachedAdMiss'
  | 'StreamAdsFetchError'
  | 'StreamPlaylistError'
  | 'StreamChunklistError'
  | 'StreamCacheReadMissError'
  // campaign
  | 'AdserverRequest'
  | 'AdserverFilled'
  | 'AdserverImpression'
  | 'AdserverComplete'
  | 'AdserverInventoryRequest'
  | 'AdserverImpressionTest'
  | 'AdserverPacedRequestTest'
  // bidder
  | 'WinningBids'
  // inventory routing
  | 'DestinationErrors'
  | 'DestinationLatency'
  | 'DestinationNoFills'
  | 'DestinationRequests'
  | 'DestinationResponses'
  | 'DestinationTimeouts'
  | 'InventoryRouteEndpointLatency'
  | 'InventoryRouteErrors'
  | 'InventoryRouteNoFills'
  | 'InventoryRouteRequests'
  | 'InventoryRouteResponses'
  | 'DestinationWins'
  // automated bid floor
  | 'BidFloor'
  | 'EstimatedRevenueOptimized'
  | 'BidFloorOptimized';

// converts the metricKeys from Aerospike query to match the ISelectedMetrics keys
// this will determine which aerospike metrics are aggregated when the query results are received
export const metricKeyToString = (
  resourceType: LiveDataResourceType,
): { [key: string]: GraphMetricKeys } => {
  if (resourceType === 'Site') {
    return {
      endpoint_request: 'EndpointRequests',
      impressions: 'Impressions',
      impressions_unpaid: 'ImpressionsUnpaid',
      bid_won: 'WinningBids',
      auction_init: 'EventAuctionInits',
      cpm: 'CPM',
      cpm_unpaid: 'CPMUnpaid',
      bid_response: 'BidResponses',
      bid_request: 'BidRequests',
    };
  } else if (resourceType === 'Stream') {
    return {
      stream_chunklist_response:
        networkHomeLiveDataKeys.STREAM_CHUNKLIST_RESPONSE_KEY as GraphMetricKeys,
      stream_start: networkHomeLiveDataKeys.STREAM_START_KEY as GraphMetricKeys,
      stream_pod_request: networkHomeLiveDataKeys.STREAM_POD_REQUEST_KEY as GraphMetricKeys,
      stream_ad_filled: networkHomeLiveDataKeys.STREAM_AD_FILLED_KEY as GraphMetricKeys,
      stream_ad_impression: networkHomeLiveDataKeys.STREAM_AD_IMPRESSION_KEY as GraphMetricKeys,
      stream_ad_complete: networkHomeLiveDataKeys.STREAM_AD_COMPLETE_KEY as GraphMetricKeys,
      stream_transcode_miss: networkHomeLiveDataKeys.STREAM_TRANSCODE_MISS_KEY as GraphMetricKeys,
    };
  } else if (resourceType === 'StreamKPI') {
    return {
      stream_ad_filled: networkHomeLiveDataKeys.STREAM_AD_FILLED_KEY as GraphMetricKeys,
      stream_pod_request: networkHomeLiveDataKeys.STREAM_POD_REQUEST_KEY as GraphMetricKeys,
      stream_pod_filled: networkHomeLiveDataKeys.STREAM_POD_FILLED_KEY as GraphMetricKeys,
      stream_ad_impression: networkHomeLiveDataKeys.STREAM_AD_IMPRESSION_KEY as GraphMetricKeys,
      stream_chunklist_fetch: networkHomeLiveDataKeys.STREAM_CHUNKLIST_FETCH_KEY as GraphMetricKeys,
      stream_chunklist_response:
        networkHomeLiveDataKeys.STREAM_CHUNKLIST_RESPONSE_KEY as GraphMetricKeys,
      stream_ad_complete: networkHomeLiveDataKeys.STREAM_AD_COMPLETE_KEY as GraphMetricKeys,
      stream_start: networkHomeLiveDataKeys.STREAM_START_KEY as GraphMetricKeys,
    };
  } else if (resourceType === 'StreamLatency') {
    return {
      stream_ad_filled: networkHomeLiveDataKeys.STREAM_AD_FILLED_KEY as GraphMetricKeys,
      stream_ad_filled_latency:
        networkHomeLiveDataKeys.STREAM_AD_FILLED_LATENCY_KEY as GraphMetricKeys,
      stream_ad_fetch_timeout:
        networkHomeLiveDataKeys.STREAM_AD_FETCH_TIMEOUT_KEY as GraphMetricKeys,
      stream_playlist_timeout:
        networkHomeLiveDataKeys.STREAM_PLAYLIST_TIMEOUT_KEY as GraphMetricKeys,
      stream_chunklist_timeout:
        networkHomeLiveDataKeys.STREAM_CHUNKLIST_TIMEOUT_KEY as GraphMetricKeys,
    };
  } else if (resourceType === 'StreamError') {
    return {
      stream_transcode_miss_error:
        networkHomeLiveDataKeys.STREAM_TRANSCODE_MISS_ERROR_KEY as GraphMetricKeys,
      stream_cached_ad_miss: networkHomeLiveDataKeys.STREAM_CACHED_AD_MISS_KEY as GraphMetricKeys,
      stream_ads_fetch_error: networkHomeLiveDataKeys.STREAM_ADS_FETCH_ERROR_KEY as GraphMetricKeys,
      stream_playlist_error: networkHomeLiveDataKeys.STREAM_PLAYLIST_ERROR_KEY as GraphMetricKeys,
      stream_chunklist_error: networkHomeLiveDataKeys.STREAM_CHUNKLIST_ERROR_KEY as GraphMetricKeys,
      stream_cache_read_miss_error:
        networkHomeLiveDataKeys.STREAM_CACHE_READ_MISS_ERROR_KEY as GraphMetricKeys,
    };
  } else if (resourceType === 'Campaign') {
    return {
      adserver_request: 'AdserverRequest',
      adserver_inventory_request: 'AdserverInventoryRequest',
      adserver_filled: 'AdserverFilled',
      adserver_impression: 'AdserverImpression',
      adserver_complete: 'AdserverComplete',
      adserver_impression_test: 'AdserverImpressionTest',
      adserver_paced_request_test: 'AdserverPacedRequestTest',
    };
  } else if (resourceType === 'Bidder') {
    return {
      cpm: 'CPM',
      cpm_unpaid: 'CPMUnpaid',
      bid_response: 'BidResponses',
      bid_request: 'BidRequests',
      bid_won: 'WinningBids',
      impressions: 'Impressions',
      impressions_unpaid: 'ImpressionsUnpaid',
    };
  } else if (resourceType === 'Deal') {
    return {
      endpoint_request: 'EndpointRequests',
      cpm: 'CPM',
      cpm_unpaid: 'CPMUnpaid',
      bid_response: 'BidResponses',
      bid_request: 'BidRequests',
      bid_won: 'WinningBids',
      impressions: 'Impressions',
      impressions_unpaid: 'ImpressionsUnpaid',
    };
  } else if (resourceType === 'InventoryRoute') {
    return {
      inventory_route_destination_error: 'DestinationErrors',
      inventory_route_destination_latency: 'DestinationLatency',
      inventory_route_destination_no_fill: 'DestinationNoFills',
      inventory_route_destination_request: 'DestinationRequests',
      inventory_route_destination_response: 'DestinationResponses',
      inventory_route_destination_timeout: 'DestinationTimeouts',
      inventory_route_destination_win: 'DestinationWins',
    };
  } else if (resourceType === 'AutomatedBidFloor') {
    return {
      bid_won_cpm: 'CPM' as GraphMetricKeys,
      bid_response: 'BidResponses' as GraphMetricKeys,
      bid_request: 'BidRequests' as GraphMetricKeys,
      bid_won: 'WinningBids' as GraphMetricKeys,
      bid_floor: 'BidFloor' as GraphMetricKeys,
    };
  } else {
    return {};
  }
};

// helper functions create string from value for KPI display
// for large values like 1,500,000 => 1.5M
const abbreviateLargeValue = (value: number) => {
  return value >= 1000000
    ? (value / 1000000).toFixed(1).toString() + 'M'
    : parseInt(value.toFixed(0), 10).toLocaleString();
};
// for large percentages (where decimal is not needed)
const createLargePercentageString = (value: number) => {
  return Math.round(value).toString() + '%';
};
// for small percentages (where decimals are needed)
const createSmallPercentageString = (value: number) => {
  return value.toFixed(1).toString() + '%';
};

const populateRealtimeAnalyticsWithTestCampaignData = (
  campaign: IAdserverCampaign,
  realtimeAnalytics: IRealtimeAnalytics[],
  densityRange: number,
) => {
  const startUnix = moment(campaign.StartDate).unix();
  const endUnix = moment(campaign.EndDate).unix();
  const totalCampaignHours = (endUnix - startUnix) / (60 * 60);

  const overallGoalPerHour =
    Number(campaign.Quantity) /
    (campaign.CampaignDurationInHours ? campaign.CampaignDurationInHours : totalCampaignHours);

  const [isDaypartingActive, DaypartingIntervals] = parseDayparting(campaign.Dayparting);

  for (let i = 0; i < realtimeAnalytics.length; i++) {
    const currAnalyticDate = moment(realtimeAnalytics[i].DateExtended);
    if (
      !isDaypartingActive ||
      CurrentlyWithinDayparting(currAnalyticDate, campaign.DaypartingIsAllowed, DaypartingIntervals)
    ) {
      let goalForThisHour = overallGoalPerHour;
      if (
        campaign.FlightDateCheckpointsEnabled === EBool.EBool_TRUE &&
        campaign.FlightDateCheckpoints.length > 0
      ) {
        for (let i = 0; i < campaign.FlightDateCheckpoints.length; i++) {
          const checkpoint = campaign.FlightDateCheckpoints[i];
          const checkpointStart = moment(checkpoint.StartDate);
          const checkpointEnd = moment(checkpoint.EndDate);
          if (
            currAnalyticDate.isBetween(checkpointStart, checkpointEnd) &&
            checkpoint.CheckpointDurationInHours
          ) {
            goalForThisHour = checkpoint.Quantity / checkpoint.CheckpointDurationInHours;
            break;
          }
        }
      }

      if (
        densityRange === DensityRange24HoursByHour ||
        densityRange === DensityRange12HoursByHour ||
        densityRange === DensityRange4HoursByHour
      ) {
        realtimeAnalytics[i].AdserverIdealImpressionCount = goalForThisHour;
      } else {
        realtimeAnalytics[i].AdserverIdealImpressionCount = goalForThisHour / 60;
      }
    } else {
      realtimeAnalytics[i].AdserverIdealImpressionCount = 0;
    }
  }
};

const getDateValue = (
  startUnixTimestamp: number,
  timezone: string,
  densityRange?: number,
): string => {
  if (startUnixTimestamp && timezone) {
    if (densityRange === DensityRangeWeekByHour) {
      return formatUnixToTimeWithDateInTimezone(startUnixTimestamp, timezone);
    } else {
      return formatUnixToTimeInTimezone(startUnixTimestamp, timezone);
    }
  }
  return '';
};

// matches the KPIs displayed at the top of the live data section
export interface ILiveDataKPIsDisplay {
  // site
  'Site Revenue'?: ILiveDataKPIsDisplayItem;
  'Site Impressions'?: ILiveDataKPIsDisplayItem;
  'Site Endpoint Requests'?: ILiveDataKPIsDisplayItem;
  'Site Render Rate'?: ILiveDataKPIsDisplayItem;
  'Site Bid Response Rate'?: ILiveDataKPIsDisplayItem;
  // stream
  'Stream Render Rate'?: ILiveDataKPIsDisplayItem;
  'Stream Complete Rate'?: ILiveDataKPIsDisplayItem;
  'Stream Miss Rate'?: ILiveDataKPIsDisplayItem;
  'Stream Impressions'?: ILiveDataKPIsDisplayItem;
  // campaigns
  'Adserver Requests'?: ILiveDataKPIsDisplayItem;
  'Adserver Inventory Requests'?: ILiveDataKPIsDisplayItem;
  'Adserver Impressions'?: ILiveDataKPIsDisplayItem;
  'Adserver Completes'?: ILiveDataKPIsDisplayItem;
  'Adserver Impressions Test'?: ILiveDataKPIsDisplayItem;
  'Adserver Paced Request Test'?: ILiveDataKPIsDisplayItem;
  'Adserver Ideal Impressions'?: ILiveDataKPIsDisplayItem;
  // bidder
  'Bidder Revenue'?: ILiveDataKPIsDisplayItem;
  'Bidder Bid Requests'?: ILiveDataKPIsDisplayItem;
  'Bidder eCPM'?: ILiveDataKPIsDisplayItem;
  'Bidder Impressions'?: ILiveDataKPIsDisplayItem;
  'Bidder Render Rate'?: ILiveDataKPIsDisplayItem;
  'Bidder Bid Response Rate'?: ILiveDataKPIsDisplayItem;
  // deal
  'Deal Revenue'?: ILiveDataKPIsDisplayItem;
  'Deal Bid Requests'?: ILiveDataKPIsDisplayItem;
  'Deal eCPM'?: ILiveDataKPIsDisplayItem;
  'Deal Impressions'?: ILiveDataKPIsDisplayItem;
  'Deal Render Rate'?: ILiveDataKPIsDisplayItem;
  'Deal Bid Response Rate'?: ILiveDataKPIsDisplayItem;
  // inventory routing
  'Inventory Route Requests'?: ILiveDataKPIsDisplayItem;
  'Inventory Route Responses'?: ILiveDataKPIsDisplayItem;
  'Inventory Route Errors'?: ILiveDataKPIsDisplayItem;
  'Inventory Route No Fills'?: ILiveDataKPIsDisplayItem;
  'Inventory Route Latency'?: ILiveDataKPIsDisplayItem;
}

// creates an empty aggregator
export const createNewAggregator = (
  startUnixTimestamp: number,
  timezone: string,
  densityRange?: number,
): IRealtimeAnalytics => {
  return {
    Date: getDateValue(startUnixTimestamp, timezone, densityRange),
    DateExtended:
      startUnixTimestamp && timezone
        ? formatUnixToTimeExtendedInTimezone(startUnixTimestamp, timezone)
        : '',
    DateTooltip:
      startUnixTimestamp && timezone
        ? formatTooltipDateInTimezone(moment.unix(startUnixTimestamp), timezone, 0)
        : '',
    Impressions: 0,
    ImpressionsUnpaid: 0,
    ImpressionAdDuration: 0,
    ImpressionAdDurationUnpaid: 0,
    EndpointRequests: 0,
    EndpointResponses: 0,
    EventAuctionInits: 0,
    Revenue: 0,
    WinningBids: 0,
    CPM: 0,
    CPMUnpaid: 0,
    EndpointLatency: 0,
    AuctionLatency: 0,
    BidderLatency: 0,
    AuctionEnd: 0,
    BidResponses: 0,
    BidRequests: 0,
    BidError: 0,
    BidTimeout: 0,
    // Stream
    StreamAdChunklistResponse: 0,
    StreamStart: 0,
    StreamPodRequest: 0,
    StreamAdFilled: 0,
    StreamAdImpression: 0,
    StreamAdComplete: 0,
    // Campaign
    AdserverRequest: 0,
    AdserverInventoryRequest: 0,
    AdserverFilled: 0,
    AdserverImpression: 0,
    AdserverComplete: 0,
    AdserverImpressionTest: 0,
    AdserverPacedRequestTest: 0,
    // Inventory Routing
    DestinationErrors: 0,
    DestinationLatency: 0,
    DestinationNoFills: 0,
    DestinationRequests: 0,
    DestinationResponses: 0,
    DestinationTimeouts: 0,
    DestinationWins: 0,
    InventoryRouteEndpointLatency: 0,
    InventoryRouteErrors: 0,
    InventoryRouteNoFills: 0,
    InventoryRouteRequests: 0,
    InventoryRouteResponses: 0,

    ABTestName: '',

    // Automated Bid Floor
    BidFloorLow: 0,
    BidFloorOptimized: 0,
    BidFloorHigh: 0,
    WinningBidsLow: 0,
    WinningBidsOptimized: 0,
    WinningBidsHigh: 0,
    CPMLow: 0,
    CPMOptimized: 0,
    CPMHigh: 0,
    BidResponsesLow: 0,
    BidResponsesOptimized: 0,
    BidResponsesHigh: 0,
    BidRequestsLow: 0,
    BidRequestsOptimized: 0,
    BidRequestsHigh: 0,
  } as IRealtimeAnalytics;
};

export interface ILiveDataKPIsDisplayItem {
  value: string;
  sign: number;
  displayName: string;
}

// the title is what shows in the dropdown, the description keys into the RealtimeAnalytics to be displayed and must
// match the key in ISelected Metrics
// defines the available site, campaign and stream metrics for the live data graph
//
// Warning: Each ID and description field !!! MUST !!! be unique
const siteMetricsList: IMenuList = {
  '#D30C7B': {
    title: 'Endpoint Requests',
    selected: false,
    ID: '#D30C7B',
    description: 'EndpointRequests',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#A34FC2': {
    title: 'Auction Inits',
    selected: false,
    ID: '#A34FC2',
    description: 'EventAuctionInits',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Impressions',
    selected: false,
    ID: '#8377D1',
    description: 'Impressions',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#4952FF': {
    title: 'Revenue',
    selected: true,
    ID: '#4952FF',
    description: 'Revenue',
    graphType: 'Bar',
    yAxisId: 'medium',
  },
  '#FBDD62': {
    title: 'CPM',
    selected: false,
    ID: '#FBDD62',
    description: 'CPM',
    graphType: 'Line',
    yAxisId: 'small',
  },
};

const streamMetricsList: IMenuList = {
  '#D30C7B': {
    title: 'Chunklist Resp',
    selected: false,
    ID: '#D30C7B',
    description: 'StreamChunklistResponse',
    graphType: 'Line',
    yAxisId: 'small',
  },
  '#A34FC2': {
    title: 'Stream Starts',
    selected: false,
    ID: '#A34FC2',
    description: 'StreamStart',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#4952FF': {
    title: 'Pod Requests',
    selected: false,
    ID: '#4952FF',
    description: 'StreamPodRequest',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#FBDD62': {
    title: 'Ads Filled',
    selected: false,
    ID: '#FBDD62',
    description: 'StreamAdFilled',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Impressions',
    selected: true,
    ID: '#8377D1',
    description: 'StreamAdImpression',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#4FC2B5': {
    title: 'Ad Completes',
    selected: false,
    ID: '#4FC2B5',
    description: 'StreamAdComplete',
    graphType: 'Line',
    yAxisId: 'big',
  },
};

const campaignMetricsList: IMenuList = {
  '#D30C7B': {
    title: 'Paced Requests',
    selected: false,
    ID: '#D30C7B',
    description: 'AdserverRequest',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#FBDD62': {
    title: 'Filled',
    selected: false,
    ID: '#FBDD62',
    description: 'AdserverFilled',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Impressions',
    selected: true,
    ID: '#8377D1',
    description: 'AdserverImpression',
    graphType: 'Line',
    yAxisId: 'small',
  },
  '#4FC2B5': {
    title: 'Completes',
    selected: false,
    ID: '#4FC2B5',
    description: 'AdserverComplete',
    graphType: 'Line',
    yAxisId: 'small',
  },
  '#cd05d7': {
    title: 'Raw Requests',
    selected: false,
    ID: '#cd05d7',
    description: 'AdserverInventoryRequest',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#FF0000': {
    title: 'Impressions With Waterfall',
    selected: false,
    ID: '#FF0000',
    description: 'AdserverImpressionTest',
    graphType: 'Line',
    yAxisId: 'small',
  },
  '#2986cc': {
    title: 'Paced Request Waterfall',
    selected: false,
    ID: '#2986cc',
    description: 'AdserverPacedRequestTest',
    graphType: 'Line',
    yAxisId: 'small',
  },
  '#a64d79': {
    title: 'Ideal Paced Evenly Impressions',
    selected: false,
    ID: '#a64d79',
    description: 'AdserverIdealImpressionCount',
    graphType: 'Line',
    yAxisId: 'small',
  },
};

const bidderMetricsList: IMenuList = {
  '#4952FF': {
    title: 'Revenue',
    selected: true,
    ID: '#4952FF',
    description: 'Revenue',
    graphType: 'Bar',
    yAxisId: 'medium',
  },
  '#D30C7B': {
    title: 'Bid Requests',
    selected: false,
    ID: '#D30C7B',
    description: 'BidRequests',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#A34FC2': {
    title: 'Bid Responses',
    selected: false,
    ID: '#A34FC2',
    description: 'BidResponses',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Winning Bids',
    selected: false,
    ID: '#8377D1',
    description: 'WinningBids',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#837AAA': {
    title: 'Impressions',
    selected: false,
    ID: '#837AAA',
    description: 'Impressions',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#FBDD62': {
    title: 'CPM',
    selected: false,
    ID: '#FBDD62',
    description: 'CPM',
    graphType: 'Line',
    yAxisId: 'small',
  },
};

const dealMetricsList: IMenuList = {
  '#4952FF': {
    title: 'Revenue',
    selected: true,
    ID: '#4952FF',
    description: 'Revenue',
    graphType: 'Bar',
    yAxisId: 'medium',
  },
  '#D30C7B': {
    title: 'Bid Requests',
    selected: false,
    ID: '#D30C7B',
    description: 'BidRequests',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#A34FC2': {
    title: 'Bid Responses',
    selected: false,
    ID: '#A34FC2',
    description: 'BidResponses',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Winning Bids',
    selected: false,
    ID: '#8377D1',
    description: 'WinningBids',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#837AAA': {
    title: 'Impressions',
    selected: false,
    ID: '#837AAA',
    description: 'Impressions',
    graphType: 'Line',
    yAxisId: 'big',
  },
  '#FBDD62': {
    title: 'CPM',
    selected: false,
    ID: '#FBDD62',
    description: 'CPM',
    graphType: 'Line',
    yAxisId: 'small',
  },
};

// colors for stackedBar metrics will be overwritten in LiveDataGraph
const inventoryRouteMetricsList: IMenuList = {
  '#4952FF': {
    title: 'Destination Requests',
    selected: true,
    ID: '#4952FF',
    description: 'DestinationRequests',
    graphType: 'StackedBar',
    yAxisId: 'big',
  },
  '#D30C7B': {
    title: 'Destination Responses',
    selected: false,
    ID: '#D30C7B',
    description: 'DestinationResponses',
    graphType: 'StackedBar',
    yAxisId: 'big',
  },
  '#A34FC2': {
    title: 'Destination Errors',
    selected: false,
    ID: '#A34FC2',
    description: 'DestinationErrors',
    graphType: 'StackedBar',
    yAxisId: 'error',
  },
  '#8377D1': {
    title: 'Destination No Fills',
    selected: false,
    ID: '#8377D1',
    description: 'DestinationNoFills',
    graphType: 'StackedBar',
    yAxisId: 'error',
  },
  '#837AAA': {
    title: 'Destination Latency',
    selected: false,
    ID: '#837AAA',
    description: 'DestinationLatency',
    graphType: 'StackedBar',
    yAxisId: 'small',
  },
  '#FBDD62': {
    title: 'Destination Timeouts',
    selected: false,
    ID: '#FBDD62',
    description: 'DestinationTimeouts',
    graphType: 'StackedBar',
    yAxisId: 'error',
  },
  '#56CBF9': {
    title: 'Destination Wins',
    selected: true,
    ID: '#56CBF9',
    description: 'DestinationWins',
    graphType: 'StackedBar',
    yAxisId: 'big',
  },
};

const automatedBidFloorMetricsList: IMenuList = {
  '#4952FF': {
    title: 'Estimated Revenue',
    selected: false,
    ID: '#4952FF',
    description: 'EstimatedRevenue',
    graphType: 'Bar',
    yAxisId: 'medium',
  },
  '#D30C7B': {
    title: 'Bid Requests',
    selected: true,
    ID: '#D30C7B',
    description: 'BidRequests',
    graphType: 'Bar',
    yAxisId: 'big',
  },
  '#A34FC2': {
    title: 'Bid Responses',
    selected: false,
    ID: '#A34FC2',
    description: 'BidResponses',
    graphType: 'Bar',
    yAxisId: 'big',
  },
  '#8377D1': {
    title: 'Winning Bids',
    selected: false,
    ID: '#8377D1',
    description: 'WinningBids',
    graphType: 'Bar',
    yAxisId: 'big',
  },
  '#FBDD62': {
    title: 'CPM',
    selected: false,
    ID: '#FBDD62',
    description: 'CPM',
    graphType: 'Bar',
    yAxisId: 'big',
  },
};

// metrics that will use a stackedBar graph
const stackedBars = [
  'inventory_route_destination_error',
  'inventory_route_destination_latency',
  'inventory_route_destination_no_fill',
  'inventory_route_destination_request',
  'inventory_route_destination_response',
  'inventory_route_destination_timeout',
  'inventory_route_destination_win',
];

export const LiveData = decorate(
  {
    styles: () => ({
      chartName: {
        fontSize: 16,
        fontWeight: 500,
      },
      input: {
        fontSize: 14,
      },
      cardTitle: {
        fontWeight: 500,
        fontSize: 12,
        color: '#292F36',
      },
      cardValue: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: 20,
        color: '#292F36',
      },
      cardTitleDisabled: {
        fontWeight: 500,
        fontSize: 12,
        color: '#cecdcf',
      },
      cardValueDisabled: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      liveDataItem: {
        marginTop: 16,
        width: '100%',
      },
      dropdownGrid: {
        marginLeft: 15,
      },
      dropdownTypography: {
        color: '#707070',
        fontSize: 14,
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      loading1: LoadingStateLoading,
      data1: [] as IRealtimeAnalytics[],
      data1raw: [] as IRealtimeAnalytics[],
      metricsList: {} as IMenuList,
      selectedMetrics: {} as ISelectedMetrics,
      selectedIDs: [] as string[],
      densityRange: this.props.resourceType === 'AutomatedBidFloor' ? 12 : 24,
      interval: undefined,
      hidden:
        this.props.bidderViewTabIndex !== undefined ? this.props.bidderViewTabIndex === 5 : false,
      liveDataKPIsDisplay: {} as ILiveDataKPIsDisplay,
      snackbarMessage: '',
      snackbarError: false,
      currencySymbol: convertECurrencyTypeToSymbol(this.props.publisher.CurrencyDisplayed),
      allBidderABTestingConfigs: {} as { [key: string]: IListItem },
      allBidderABTestingTests: {} as { [key: string]: IListItem },
      currentBidderABTestingName: '',
      filterBidderABTestingName: false,
      allChannelABTestingConfigs: {} as { [key: string]: IListItem },
      allChannelABTestingTests: {} as { [key: string]: IListItem },
      currentChannelABTestingName: '',
      filterChannelABTestingName: false,
      stackedBarMetrics: {},
      // optional filter info
      includeFilter: false,
      filterTitle: '',
      doNotDisplayTitleOptions: [],
      filterMenuTitle: '',
    };

    componentDidMount() {
      this.loadRealtimeAnalytics();
      switch (this.props.resourceType) {
        case 'Stream':
          this.setState({
            includeFilter: true,
            filterTitle: 'Inventory Splits',
            doNotDisplayTitleOptions: ['StreamStart', 'StreamChunklistResponse'],
            filterMenuTitle: 'Network Channels',
          });
          break;
        case 'InventoryRoute':
          this.setState({
            densityRange: 12,
            includeFilter: true,
            filterTitle: 'Destinations',
            filterMenuTitle: 'Destinations',
          });
          break;
      }
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
      if (this.props.resourceIDs !== prevProps.resourceIDs) {
        clearInterval(this.state.interval);
        this.loadRealtimeAnalytics();
      }
      if (prevProps.bidderViewTabIndex !== this.props.bidderViewTabIndex) {
        // hide livegraph if on automated bid floor tab
        this.setState({ hidden: this.props.bidderViewTabIndex === 5 });
      }
    }

    // clears the interval for the live data api call
    componentWillUnmount() {
      clearInterval(this.state.interval);
    }

    loadRealtimeAnalytics() {
      // load real time analytics
      this.loadDensityData();

      // build dropdown lists
      this.generateDropdowns();

      // set interval for API call
      const interval = setInterval(() => {
        this.loadDensityData(true);
      }, 30000);

      this.setState({
        interval,
      });
    }

    // generates metrics dropdown and sets the starting selected metric
    generateDropdowns() {
      const { headerbidders, resourceType, resourceIDs, sites } = this.props;

      const allBidderABTestingConfigs = {} as { [key: string]: IListItem };
      const allBidderABTestingTests = {} as { [key: string]: IListItem };
      const allChannelABTestingConfigs = {} as { [key: string]: IListItem };
      const allChannelABTestingTests = {} as { [key: string]: IListItem };

      let metricsList = {} as IMenuList;
      let selectedMetrics = {} as ISelectedMetrics;

      if (resourceType === 'Site') {
        metricsList = siteMetricsList;
        selectedMetrics = {
          Revenue: { color: '#4952FF', graphType: 'Bar', yAxisId: 'medium' },
        };

        if (sites) {
          const foundChannel = sites.find((Channel) => '' + Channel.ID === resourceIDs);
          if (foundChannel) {
            for (const abTestingConfig of foundChannel.abTests) {
              allChannelABTestingConfigs[abTestingConfig.ID] = {
                ID: abTestingConfig.ID,
                title: abTestingConfig.Name,
                selected: false,
              } as any;

              for (const abTestingTest of abTestingConfig.Tests) {
                allChannelABTestingTests[abTestingTest.ID] = {
                  ID: abTestingTest.ID,
                  title: abTestingTest.Name,
                  selected: false,
                } as any;
              }
            }
          }
        }
      } else if (resourceType === 'Stream') {
        metricsList = streamMetricsList;
        selectedMetrics = {
          StreamAdImpression: { color: '#8377D1', graphType: 'Line', yAxisId: 'big' },
        };
      } else if (resourceType === 'Campaign') {
        metricsList =
          this.props.userInfo !== undefined && hasRoleSuperUser(this.props.userInfo)
            ? campaignMetricsList
            : omit(campaignMetricsList, ['#FF0000', '#2986cc', '#a64d79']);
        selectedMetrics = {
          AdserverImpression: { color: '#8377D1', graphType: 'Line', yAxisId: 'small' },
        };
      } else if (resourceType === 'Bidder') {
        metricsList = bidderMetricsList;
        selectedMetrics = {
          Revenue: { color: '#4952FF', graphType: 'Bar', yAxisId: 'medium' },
        };

        if (headerbidders) {
          const foundHeaderbidder = headerbidders.find(
            (headerbidder) => headerbidder.ID === resourceIDs,
          );
          if (foundHeaderbidder) {
            for (const abTestingConfig of foundHeaderbidder.abTests) {
              allBidderABTestingConfigs[abTestingConfig.ID] = {
                ID: abTestingConfig.ID,
                title: abTestingConfig.Name,
                selected: false,
              } as any;

              for (const abTestingTest of abTestingConfig.Tests) {
                allBidderABTestingTests[abTestingTest.ID] = {
                  ID: abTestingTest.ID,
                  title: abTestingTest.Name,
                  selected: false,
                } as any;
              }
            }
          }
        }
      } else if (resourceType === 'Deal') {
        metricsList = dealMetricsList;
        selectedMetrics = {
          Revenue: { color: '#4952FF', graphType: 'Bar', yAxisId: 'medium' },
        };
      } else if (resourceType === 'InventoryRoute') {
        metricsList = inventoryRouteMetricsList;
        selectedMetrics = {
          // stacked bar colors will be assigned in LiveDataGraph
          DestinationRequests: { color: '', graphType: 'StackedBar', yAxisId: 'big' },
          DestinationWins: { color: '', graphType: 'StackedBar', yAxisId: 'big' },
        };
      } else if (resourceType === 'AutomatedBidFloor') {
        metricsList = automatedBidFloorMetricsList;
        selectedMetrics = {
          BidRequestsLow: { color: graphColors.cyan, graphType: 'Bar', yAxisId: 'big' },
          BidRequestsOptimized: { color: graphColors.lightBlue, graphType: 'Bar', yAxisId: 'big' },
          BidRequestsHigh: { color: graphColors.turquoise, graphType: 'Bar', yAxisId: 'big' },
          BidFloorOptimized: {
            color: graphColors.lightOrange,
            graphType: 'Line',
            yAxisId: 'small',
          },
        };
      }

      this.setState({
        allBidderABTestingConfigs,
        allBidderABTestingTests,
        allChannelABTestingConfigs,
        allChannelABTestingTests,
        metricsList,
        selectedMetrics,
      });
    }

    convertLiveDataExchangeRate = (data: IRealtimeAnalytics[]): IRealtimeAnalytics[] => {
      for (const datum of data) {
        datum.Revenue = datum.Revenue * this.props.publisher.ExchangeRate.ExchangeValueToUSD;
        datum.CPM = datum.CPM * this.props.publisher.ExchangeRate.ExchangeValueToUSD;
      }
      return data;
    };

    // aggregates incoming data into realtimeAnalytics
    aggregateRealtimeAnalytics = (
      realtimeAnalytics: IRealtimeAnalytics[],
      data: IDimensions,
      endUnixTimestamp: number,
      startUnixTimestamp: number,
      itemskey: string,
      refresh: boolean,
    ) => {
      const { timezone, resourceType } = this.props;
      const { densityRange } = this.state;
      let stackedBarMetrics: { [key: string]: string[] };
      this.state.stackedBarMetrics
        ? (stackedBarMetrics = Object.assign({}, this.state.stackedBarMetrics))
        : (stackedBarMetrics = {});
      const minuteDifference = moment
        .unix(endUnixTimestamp)
        .diff(moment.unix(startUnixTimestamp), 'minutes');
      // get the metric keys for this resource
      const currentMetricKeys = metricKeyToString(resourceType);
      const selectedMetricsKeys = Object.keys(currentMetricKeys);
      // calculate the number of periods based on minute difference and period duration
      // each period is 1 minute long except the the hour by hour ranges
      let periodDuration = 1;
      let lastPeriodDuration = 1;
      if (
        densityRange === DensityRange4HoursByHour ||
        densityRange === DensityRange12HoursByHour ||
        densityRange === DensityRange24HoursByHour ||
        densityRange === DensityRangeWeekByHour
      ) {
        periodDuration = 60;
        lastPeriodDuration = (endUnixTimestamp - startUnixTimestamp) / 60;
      }
      let numPeriods = Math.ceil(minuteDifference / periodDuration);
      if (
        densityRange === DensityRange4HoursByHour ||
        densityRange === DensityRange12HoursByHour ||
        densityRange === DensityRange24HoursByHour ||
        densityRange === DensityRangeWeekByHour
      ) {
        // if 4/12 hours by hour, 1 period per hour, refresh latest hour
        numPeriods = refresh ? 1 : densityRange;
      }
      // for each period determine what metrics need to be summed
      const idsMap = {} as { [key: string]: boolean };
      for (let period = 0; period < numPeriods; period++) {
        // create new aggregator and increment time stamp
        const realtimeAnalytic = createNewAggregator(startUnixTimestamp, timezone, densityRange);
        startUnixTimestamp += periodDuration * 60;
        // if it is the last period, use the last period duration
        for (
          let minute = 0;
          minute < (period === numPeriods - 1 ? lastPeriodDuration : periodDuration);
          minute++
        ) {
          const currentIndex = minute + periodDuration * period;
          if (itemskey in data) {
            for (const [id, item] of Object.entries(data[itemskey].items)) {
              idsMap[id] = true;
              const metrics = item.metrics as IItemMetrics;
              // iterate over selected metrics to key into the response
              for (let i = 0; i < selectedMetricsKeys.length; i++) {
                const selectedMetric = selectedMetricsKeys[i] as AerospikeMetricKeys;
                if (selectedMetric in metrics) {
                  const selectedMetricString = currentMetricKeys[selectedMetric];
                  let metricString = selectedMetricString as GraphMetricKeys;
                  // track the groups of stackedBarMetrics for building the stacked bar in LiveDataGraph
                  // keep even those with null data for tooltip display
                  if (stackedBars.includes(selectedMetric)) {
                    metricString = (metricString + id.toString()) as GraphMetricKeys;
                    if (!stackedBarMetrics[selectedMetricString]) {
                      stackedBarMetrics[selectedMetricString] = [];
                    }
                    if (!stackedBarMetrics[selectedMetricString].includes(metricString)) {
                      stackedBarMetrics[selectedMetricString].push(metricString);
                    }
                  }

                  // data can possibly be null for sites that have no live data
                  if (metrics[selectedMetric].data) {
                    const dataArray = metrics[selectedMetric].data;
                    // check to make sure data array can be indexed and its not null
                    if (
                      dataArray &&
                      dataArray.length > currentIndex &&
                      selectedMetric in currentMetricKeys
                    ) {
                      // if this metric will have a stackedBar graph, need to add it + the id of the corresponding item to realtimeAnalytic
                      // so that the datapoints can later be grouped into one bar
                      if (!realtimeAnalytic[metricString]) {
                        realtimeAnalytic[metricString] = 0;
                      }
                      realtimeAnalytic[metricString] += dataArray[currentIndex];
                    }
                  }
                }
              }
            }
          }

          // need to include route data in realtimeAnalytic for KPIs, even though itemskey is 'destination_id'
          if (this.props.resourceType === 'InventoryRoute') {
            if ('inventory_route_id' in data) {
              const routeMetrics: { [key: string]: GraphMetricKeys } = {
                inventory_route_endpoint_latency: 'InventoryRouteEndpointLatency',
                inventory_route_error: 'InventoryRouteErrors',
                inventory_route_no_fill: 'InventoryRouteNoFills',
                inventory_route_request: 'InventoryRouteRequests',
                inventory_route_response: 'InventoryRouteResponses',
              };
              const routeMetricsKeys = Object.keys(routeMetrics);

              for (const item of Object.values(data.inventory_route_id.items)) {
                const metrics = item.metrics as IItemMetrics;
                // iterate over selected metrics to key into the response
                for (let i = 0; i < routeMetricsKeys.length; i++) {
                  const selectedMetric = routeMetricsKeys[i] as AerospikeMetricKeys;
                  // data can possibly be null for sites that have no live data
                  if (selectedMetric in metrics && metrics[selectedMetric].data) {
                    const dataArray = metrics[selectedMetric].data;
                    // check to make sure data array can be indexed and its not null
                    if (
                      dataArray &&
                      dataArray.length > currentIndex &&
                      selectedMetric in routeMetrics
                    ) {
                      const metricString = routeMetrics[selectedMetric] as GraphMetricKeys;
                      realtimeAnalytic[metricString] += dataArray[currentIndex];
                    }
                  }
                }
              }
            }
          }
        }
        realtimeAnalytics.push(realtimeAnalytic);
      }
      this.setState({
        stackedBarMetrics,
      });
      return this.calculateDependentMetrics(realtimeAnalytics, Object.keys(idsMap));
    };

    aggregateAutomatedBidFloorRealtimeAnalytics = (
      realtimeAnalytics: IRealtimeAnalytics[],
      data: IDimensions,
      endUnixTimestamp: number,
      startUnixTimestamp: number,
      itemskey: string,
    ) => {
      const { timezone, resourceType } = this.props;
      const minuteDifference = moment
        .unix(endUnixTimestamp)
        .diff(moment.unix(startUnixTimestamp), 'minutes');
      // get the metric keys for this resource
      const currentMetricKeys = metricKeyToString(resourceType);
      const selectedMetricsKeys = Object.keys(currentMetricKeys);
      // calculate the number of periods based on minute difference and period duration
      // each period is 30 minute long
      const periodDuration = 30;
      const numPeriods = Math.ceil(minuteDifference / periodDuration);

      for (let period = 0; period < numPeriods; period++) {
        // create new aggregator and increment time stamp
        const realtimeAnalytic = createNewAggregator(startUnixTimestamp, timezone);
        startUnixTimestamp += 60 * 30;
        if (itemskey in data) {
          for (const [id, item] of Object.entries(data[itemskey].items)) {
            const metrics = item.metrics as IItemMetrics;
            for (let i = 0; i < selectedMetricsKeys.length; i++) {
              const selectedMetric = selectedMetricsKeys[i] as AerospikeMetricKeys;
              if (selectedMetric in metrics) {
                const selectedMetricString = currentMetricKeys[selectedMetric];
                let metricString = selectedMetricString;
                if (id[id.length - 1] === '2') {
                  metricString += 'Low';
                } else if (id[id.length - 1] === '1') {
                  metricString += 'Optimized';
                } else if (id[id.length - 1] === '3') {
                  metricString += 'High';
                }
                if (metrics[selectedMetric].data) {
                  const dataArray = metrics[selectedMetric].data;
                  if (dataArray && selectedMetric in currentMetricKeys) {
                    realtimeAnalytic[metricString] = dataArray[period];
                  }
                }
              }
            }
          }
        }
        realtimeAnalytics.push(realtimeAnalytic);
      }
      return this.calculateAutomatedBidFloorDependentMetrics(realtimeAnalytics);
    };

    trackEvent = (eventTitle: LiveDataEventTypes, time_to_load_sec = 0): void => {
      if (!this.props.userInfo) {
        return;
      }
      const is_super_user = hasRoleSuperUser(this.props.userInfo);
      if (this.props.resourceType === 'Bidder') {
        if (eventTitle === 'Live Data - Live Data Loaded') {
          track(
            'Bidder Settings - Live Data Loaded',
            {
              is_super_user,
              time_to_load_sec,
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Hide Live Data') {
          track(
            'Bidder Settings - Hide Live Data',
            {
              is_super_user,
              hide_live_data: !this.state.hidden,
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Change Live Data Metrics') {
          track(
            'Bidder Settings - Change Live Data Metrics',
            {
              is_super_user,
              metrics: Object.keys(this.state.selectedMetrics),
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Change Live Data Range') {
          track(
            'Bidder Settings - Change Live Data Range',
            {
              is_super_user,
              density_range: this.state.densityRange,
            },
            this.props.userInfo,
          );
        }
      } else if (this.props.resourceType === 'Site') {
        if (eventTitle === 'Live Data - Live Data Loaded') {
          track(
            'Channel Settings - Live Data Loaded',
            {
              is_super_user,
              time_to_load_sec,
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Hide Live Data') {
          track(
            'Channel Settings - Hide Live Data',
            {
              is_super_user,
              hide_live_data: !this.state.hidden,
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Change Live Data Metrics') {
          track(
            'Channel Settings - Change Live Data Metrics',
            {
              is_super_user,
              metrics: Object.keys(this.state.selectedMetrics),
            },
            this.props.userInfo,
          );
        } else if (eventTitle === 'Live Data - Change Live Data Range') {
          track(
            'Channel Settings - Change Live Data Range',
            {
              is_super_user,
              density_range: this.state.densityRange,
            },
            this.props.userInfo,
          );
        }
      }
    };

    // query realtime analytics and compile data for display on graph
    // refresh is called after the initial call to loadDensityData
    loadDensityData = (refresh?: boolean): void => {
      const { headerbidders, resourceType, resourceIDs, sites } = this.props;
      const {
        densityRange,
        currentBidderABTestingName,
        filterBidderABTestingName,
        currentChannelABTestingName,
        filterChannelABTestingName,
      } = this.state;

      // create start and end time stamps and strings for the query
      // refresh the last 5 minutes, otherwise use density range
      const startMinAgo = refresh ? -5 : -1 * densityRange + 1;
      let startUnixTimestamp = moment().add(startMinAgo, 'minute').unix();
      // if densityRange === DensityRange4HoursByHour (4 hours ago by hour), find the starting minute 3 hours ago or 1 hour ago for the refresh
      if (
        densityRange === DensityRange4HoursByHour ||
        densityRange === DensityRange12HoursByHour ||
        densityRange === DensityRange24HoursByHour ||
        densityRange === DensityRangeWeekByHour
      ) {
        if (refresh) {
          startUnixTimestamp = moment().minute(0).unix();
        } else {
          startUnixTimestamp = moment()
            .minute(0)
            .add(-densityRange + 1, 'hours')
            .unix();
        }
      }
      const beginTimeStr = '&begin_time=' + startUnixTimestamp.toString();
      const endUnixTimestamp = moment().add(1, 'minutes').unix();
      const endTimeStr = '&end_time=' + endUnixTimestamp;

      // query the metrics for the given resource type and use the itemskey provided to key into the aerospike response
      let itemskey = '',
        selectedIDs = '',
        metrics = '';
      if (resourceType === 'Site' && !filterChannelABTestingName) {
        metrics = '&metrics=1,2,3,4,5,6,7,8,9,10,11,12,13,14,40,41,42,43';
        itemskey = 'site';
      } else if (resourceType === 'Site' && filterChannelABTestingName) {
        metrics = '&metrics=1,2,3,4,5,6,7,8,9,10,11,12,13,14,40,41,42,43';
        itemskey = 'ab_test_site';
      } else if (resourceType === 'Stream') {
        metrics = '&metrics=22,23,24,25,26,27,28,29,30,31,32,33,34,35,36,37,38';
        itemskey = 'network_id';
        if (this.state.selectedIDs.length > 0) {
          selectedIDs = this.state.selectedIDs.join(',');
          itemskey = 'network_channel_id';
        }
      } else if (resourceType === 'Campaign') {
        metrics = '&metrics=15,16,17,18,19,20,21,39,84,85';
        itemskey = 'campaign_id';
      } else if (resourceType === 'Bidder' && !filterBidderABTestingName) {
        metrics = '&metrics=5,9,12,2,3,40,41,42,43'; // TODO
        itemskey = 'bidder';
      } else if (resourceType === 'Bidder' && filterBidderABTestingName) {
        metrics = '&metrics=5,9,12,2,3,40,41,42,43';
        itemskey = 'ab_test_bidder';
      } else if (resourceType === 'Deal') {
        metrics = '&metrics=5,9,12,2,3,40,41,42,43';
        itemskey = 'publica_deal_id';
      } else if (resourceType === 'InventoryRoute') {
        metrics = '&metrics=50,51,52,53,54,55,56,57,58,59,60,61';
        itemskey = 'destination_id';
        selectedIDs = this.state.selectedIDs.join(',');
      } else if (resourceType === 'AutomatedBidFloor') {
        metrics = '&metrics=3,9,12,73,75';
        itemskey = 'automated_bidfloor_id_bidfloor_type';
      }

      // construct the test bidder ID maps using the headerbidders
      const testBidderIDMapFiltered = {} as { [key: string]: string[] };
      const testBidderIDMapOrig = {} as { [key: string]: string[] };

      if (headerbidders !== undefined) {
        for (const headerbidder of headerbidders) {
          if (filterBidderABTestingName) {
            // construct the bidder map for the filter
            const filteredABTestIDs = flatten(
              headerbidder.abTests
                .filter((abTestingConfig) => abTestingConfig.BidderID.toString() === resourceIDs)
                .map((abTestingConfig) => abTestingConfig.Tests),
            )
              .filter((abTestingTest) => (abTestingTest.Name || 'A') === currentBidderABTestingName)
              .map((abTestingTest) => abTestingTest.ID.toString());

            if (filteredABTestIDs.length > 0) {
              testBidderIDMapFiltered[headerbidder.ID] = filteredABTestIDs;
            }
          } else {
            // construct the bidder map for the original test (unfiltered)
            const filteredABTestIDs = flatten(
              headerbidder.abTests
                .filter((abTestingConfig) => abTestingConfig.BidderID.toString() === resourceIDs)
                .map((abTestingConfig) => abTestingConfig.Tests),
            ).map((abTestingTest) => abTestingTest.ID.toString());

            if (filteredABTestIDs.length > 0) {
              testBidderIDMapOrig[headerbidder.ID] = filteredABTestIDs;
            }
          }
        }
      }

      // construct the test channel ID maps using the channels
      const testChannelIDMapFiltered = {} as { [key: string]: string[] };
      const testChannelIDMapOrig = {} as { [key: string]: string[] };

      if (sites !== undefined) {
        for (const channel of sites) {
          if (filterChannelABTestingName) {
            // construct the channel map for the filter
            const filteredABTestIDs = flatten(
              channel.abTests
                .filter(
                  (abTestingConfig) =>
                    abTestingConfig.ChannelID.toString() === resourceIDs &&
                    abTestingConfig.Active === 2,
                )
                .map((abTestingConfig) => abTestingConfig.Tests),
            )
              .filter(
                (abTestingTest) => (abTestingTest.Name || 'A') === currentChannelABTestingName,
              )
              .map((abTestingTest) => abTestingTest.ID.toString());

            if (filteredABTestIDs.length > 0) {
              testChannelIDMapFiltered[channel.ID] = filteredABTestIDs;
            }
          } else {
            // construct the channel map for the original test (unfiltered)
            const filteredABTestIDs = flatten(
              channel.abTests
                .filter(
                  (abTestingConfig) =>
                    abTestingConfig.ChannelID.toString() === resourceIDs &&
                    abTestingConfig.Active === 2,
                )
                .map((abTestingConfig) => abTestingConfig.Tests),
            ).map((abTestingTest) => abTestingTest.ID.toString());

            if (filteredABTestIDs.length > 0) {
              testChannelIDMapOrig[channel.ID] = filteredABTestIDs;
            }
          }
        }
      }

      // construct a query which is built using the filtered test bidder IDs
      const realtimePromiseFiltered: Promise<[any?, (Error | undefined)?]> = new Promise(
        (resolve, reject) => {
          queryRealtimeAnalytics(
            beginTimeStr,
            endTimeStr,
            `&site_ids=${resourceType === 'Site' ? resourceIDs : ''}`,
            '&biddernames=',
            '&bidder_ids=' + (resourceType === 'Bidder' ? resourceIDs : ''),
            `&test_site_ids=${JSON.stringify(testChannelIDMapFiltered)}`,
            '&site_biddernames=',
            '&site_bidder=',
            // use network ID if stream and no selected IDs
            '&network_ids=' +
              (resourceType === 'Stream' && selectedIDs.length === 0 ? resourceIDs : ''),
            // use network channel IDs if there are selected network channel IDs
            '&network_channel_ids=' +
              (resourceType === 'Stream' && selectedIDs.length !== 0 ? selectedIDs : ''),
            '&campaign_ids=' + (resourceType === 'Campaign' ? resourceIDs : ''),
            '',
            '',
            metrics,
            this.props.publisher.ID,
            false,
            `&test_bidder_ids=${JSON.stringify(testBidderIDMapFiltered)}`,
            '&deal_ids=' + (resourceType === 'Deal' ? resourceIDs : ''),
            '&inventory_route_ids=' + (resourceType === 'InventoryRoute' ? resourceIDs : ''),
            '&destination_ids=' + (resourceType === 'InventoryRoute' ? selectedIDs : ''),
            '&biddername_device_type=',
            '&automated_bid_floor_ids=' + (resourceType === 'AutomatedBidFloor' ? resourceIDs : ''),
            '&by_abf_period=' + (resourceType === 'AutomatedBidFloor' ? 'true' : ''),
          )
            .then((realtimeResponseFiltered) => resolve(realtimeResponseFiltered))
            .catch((err) => reject(err));
        },
      );

      // construct a query which is built using the original bidder test IDs
      const realtimePromiseOrig: Promise<[any?, (Error | undefined)?]> = new Promise(
        (resolve, reject) => {
          queryRealtimeAnalytics(
            beginTimeStr,
            endTimeStr,
            `&site_ids=${resourceType === 'Site' ? resourceIDs : ''}`,
            '&biddernames=',
            '&bidder_ids=' + (resourceType === 'Bidder' ? resourceIDs : ''),
            `&test_site_ids=${JSON.stringify(testChannelIDMapOrig)}`,
            '&site_biddernames=',
            '&site_bidder=',
            // use network ID if stream and no selected IDs
            '&network_ids=' +
              (resourceType === 'Stream' && selectedIDs.length === 0 ? resourceIDs : ''),
            // use network channel IDs if there are selected network channel IDs
            '&network_channel_ids=' +
              (resourceType === 'Stream' && selectedIDs.length !== 0 ? selectedIDs : ''),
            '&campaign_ids=' + (resourceType === 'Campaign' ? resourceIDs : ''),
            '',
            '',
            metrics,
            this.props.publisher.ID,
            false,
            `&test_bidder_ids=${JSON.stringify(testBidderIDMapOrig)}`,
            '&deal_ids=' + (resourceType === 'Deal' ? resourceIDs : ''),
            '&inventory_route_ids=' + (resourceType === 'InventoryRoute' ? resourceIDs : ''),
            '&destination_ids=' + (resourceType === 'InventoryRoute' ? selectedIDs : ''),
            '&biddername_device_type=',
            '&automated_bid_floor_ids=' + (resourceType === 'AutomatedBidFloor' ? resourceIDs : ''),
            '&by_abf_period=' + (resourceType === 'AutomatedBidFloor' ? 'true' : ''),
          )
            .then((realtimeResponseOrig) => resolve(realtimeResponseOrig))
            .catch((err) => reject(err));
        },
      );

      const startTime = moment().unix();
      const realtimePromises = filterBidderABTestingName
        ? [realtimePromiseFiltered]
        : [realtimePromiseOrig];

      Promise.all(realtimePromises).then(
        (response) => {
          const realtimeResponse = response[0];
          if (!refresh) {
            this.trackEvent('Live Data - Live Data Loaded', moment().unix() - startTime);
          }

          // calculate the realtime analytics for the query
          const [dimensions, error] = realtimeResponse;

          if (error || dimensions.dimensions === undefined) {
            this.setState({
              loading1: LoadingStateNoData,
            });
            return;
          }

          let realtimeAnalytics = [] as IRealtimeAnalytics[];
          if (resourceType === 'AutomatedBidFloor') {
            // TODO: aggregateAutomatedBidFloorRealtimeAnalytics has only 5 arguments, which means that it doesn't support refresh
            realtimeAnalytics = this.aggregateAutomatedBidFloorRealtimeAnalytics(
              realtimeAnalytics,
              dimensions.dimensions,
              endUnixTimestamp,
              startUnixTimestamp,
              itemskey,
            );
          } else {
            realtimeAnalytics = this.aggregateRealtimeAnalytics(
              realtimeAnalytics,
              dimensions.dimensions,
              endUnixTimestamp,
              startUnixTimestamp,
              itemskey,
              !!refresh,
            );
          }

          // data1 is used in the graph
          realtimeAnalytics = this.convertLiveDataExchangeRate(realtimeAnalytics);

          if (
            this.props.userInfo !== undefined &&
            hasRoleSuperUser(this.props.userInfo) &&
            this.props.resourceType === 'Campaign' &&
            this.props.campaign !== undefined
          ) {
            populateRealtimeAnalyticsWithTestCampaignData(
              this.props.campaign,
              realtimeAnalytics,
              densityRange,
            );
          }

          let data1 = [...realtimeAnalytics];
          // data1 raw (1 min periods) is for densityRange 240's refresh
          let data1raw = [] as IRealtimeAnalytics[];
          if (refresh) {
            if (densityRange === DensityRange4HoursByMinutes) {
              // if 240 density range, use raw data from previous query (1 min blocks) to add refresh data
              // then combine the data into 4 min blocks for graph
              data1raw = this.addRefreshData(this.state.data1raw, realtimeAnalytics);
              data1 = this.combineData(data1raw, densityRange);
            } else {
              // if refresh and any other density range, add the new minutes to the old data
              data1 = this.addRefreshData(this.state.data1, realtimeAnalytics);
            }
          } else {
            // if initial query, for 240 density range, combine the data into 4 min block
            if (densityRange === DensityRange4HoursByMinutes) {
              data1raw = realtimeAnalytics;
              data1 = this.combineData(data1, densityRange);
            }
          }

          // create the KPIs using the response
          const liveDataKPIsDisplay = this.createKPIDisplay(data1);

          this.setState({
            loading1: LoadingStateHasData,
            data1,
            data1raw,
            liveDataKPIsDisplay,
          });
        },
        () => {
          this.setState({
            loading1: LoadingStateNoData,
          });
        },
      );
    };

    // creates display data from query results to calculate percentage metrics
    createKPIDisplay = (data1: IRealtimeAnalytics[]): ILiveDataKPIsDisplay => {
      // site
      let revenue = 0,
        impressions = 0,
        endpointRequests = 0,
        winningBids = 0,
        bidResponses = 0,
        bidRequests = 0;
      // stream
      let streamAdFilled = 0,
        streamAdImpression = 0,
        streamAdComplete = 0,
        streamTranscodeMiss = 0;
      // campaign
      let adserverRequest = 0,
        adserverImpression = 0,
        adserverComplete = 0,
        adserverInventoryRequest = 0,
        adserverImpressionTest = 0,
        adserverPacedRequestTest = 0,
        adserverIdealImpressionCount = 0;
      // inventory routing
      let inventoryRouteRequests = 0,
        inventoryRouteResponses = 0,
        inventoryRouteErrors = 0,
        inventoryRouteNoFills = 0,
        inventoryRouteEndpointLatency = 0;
      data1.forEach((datum) => {
        if (this.props.resourceType === 'Site') {
          revenue += isNaN(datum.Revenue) ? 0 : datum.Revenue;
          impressions += isNaN(datum.Impressions) ? 0 : datum.Impressions;
          endpointRequests += isNaN(datum.EndpointRequests) ? 0 : datum.EndpointRequests;
          winningBids += isNaN(datum.WinningBids) ? 0 : datum.WinningBids;
          bidResponses += isNaN(datum.BidResponses) ? 0 : datum.BidResponses;
          bidRequests += isNaN(datum.BidRequests) ? 0 : datum.BidRequests;
        } else if (this.props.resourceType === 'Stream') {
          streamAdFilled +=
            datum.StreamAdFilled === undefined || isNaN(datum.StreamAdFilled)
              ? 0
              : datum.StreamAdFilled;
          streamAdImpression +=
            datum.StreamAdImpression === undefined || isNaN(datum.StreamAdImpression)
              ? 0
              : datum.StreamAdImpression;
          streamAdComplete +=
            datum.StreamAdComplete === undefined || isNaN(datum.StreamAdComplete)
              ? 0
              : datum.StreamAdComplete;
          streamTranscodeMiss +=
            datum.StreamTranscodeMiss === undefined || isNaN(datum.StreamTranscodeMiss)
              ? 0
              : datum.StreamTranscodeMiss;
        } else if (this.props.resourceType === 'Campaign') {
          adserverRequest +=
            datum.AdserverRequest === undefined || isNaN(datum.AdserverRequest)
              ? 0
              : datum.AdserverRequest;
          adserverInventoryRequest +=
            datum.AdserverInventoryRequest === undefined || isNaN(datum.AdserverInventoryRequest)
              ? 0
              : datum.AdserverInventoryRequest;
          adserverImpression +=
            datum.AdserverImpression === undefined || isNaN(datum.AdserverImpression)
              ? 0
              : datum.AdserverImpression;
          adserverComplete +=
            datum.AdserverComplete === undefined || isNaN(datum.AdserverComplete)
              ? 0
              : datum.AdserverComplete;
          adserverImpressionTest +=
            datum.AdserverImpressionTest === undefined || isNaN(datum.AdserverImpressionTest)
              ? 0
              : datum.AdserverImpressionTest;
          adserverPacedRequestTest +=
            datum.AdserverPacedRequestTest === undefined || isNaN(datum.AdserverPacedRequestTest)
              ? 0
              : datum.AdserverPacedRequestTest;
          adserverIdealImpressionCount +=
            datum.AdserverIdealImpressionCount === undefined ||
            isNaN(datum.AdserverIdealImpressionCount)
              ? 0
              : datum.AdserverIdealImpressionCount;
        } else if (this.props.resourceType === 'Bidder') {
          revenue += isNaN(datum.Revenue) ? 0 : datum.Revenue;
          impressions += isNaN(datum.Impressions) ? 0 : datum.Impressions;
          endpointRequests += isNaN(datum.EndpointRequests) ? 0 : datum.EndpointRequests;
          winningBids += isNaN(datum.WinningBids) ? 0 : datum.WinningBids;
          bidResponses += isNaN(datum.BidResponses) ? 0 : datum.BidResponses;
          bidRequests += isNaN(datum.BidRequests) ? 0 : datum.BidRequests;
        } else if (this.props.resourceType === 'Deal') {
          revenue += isNaN(datum.Revenue) ? 0 : datum.Revenue;
          impressions += isNaN(datum.Impressions) ? 0 : datum.Impressions;
          endpointRequests += isNaN(datum.EndpointRequests) ? 0 : datum.EndpointRequests;
          winningBids += isNaN(datum.WinningBids) ? 0 : datum.WinningBids;
          bidResponses += isNaN(datum.BidResponses) ? 0 : datum.BidResponses;
          bidRequests += isNaN(datum.BidRequests) ? 0 : datum.BidRequests;
        } else if (this.props.resourceType === 'InventoryRoute') {
          inventoryRouteRequests += isNaN(datum.InventoryRouteRequests)
            ? 0
            : datum.InventoryRouteRequests;
          inventoryRouteResponses += isNaN(datum.InventoryRouteResponses)
            ? 0
            : datum.InventoryRouteResponses;
          inventoryRouteErrors += isNaN(datum.InventoryRouteErrors)
            ? 0
            : datum.InventoryRouteErrors;
          inventoryRouteNoFills += isNaN(datum.InventoryRouteNoFills)
            ? 0
            : datum.InventoryRouteNoFills;
          inventoryRouteEndpointLatency += isNaN(datum.InventoryRouteEndpointLatency)
            ? 0
            : datum.InventoryRouteEndpointLatency;
        }
      });
      // calculate site metrics
      const renderRate = winningBids > 0 ? (impressions / winningBids) * 100 : 0;
      const bidResponseRate = bidRequests > 0 ? (bidResponses / bidRequests) * 100 : 0;
      // calculate stream metrics
      const streamRenderRate = streamAdFilled > 0 ? (streamAdImpression / streamAdFilled) * 100 : 0;
      const streamCompleteRate =
        streamAdImpression > 0 ? (streamAdComplete / streamAdImpression) * 100 : 0;
      const streamMissRate =
        streamAdFilled + streamTranscodeMiss > 0
          ? (streamTranscodeMiss / (streamAdFilled + streamTranscodeMiss)) * 100
          : 0;
      // calculate route latencies
      const inventoryRouteEndpointLatencyRate =
        inventoryRouteResponses > 0 ? inventoryRouteEndpointLatency / inventoryRouteResponses : 0;

      let liveDataKPIsDisplay: ILiveDataKPIsDisplay = {};
      // populate the display data (note: sign is for comparability which is not yet enabled in live data)
      if (this.props.resourceType === 'Site') {
        liveDataKPIsDisplay = {
          'Site Revenue': {
            value: this.state.currencySymbol + abbreviateLargeValue(revenue),
            sign: 1,
            displayName: 'Revenue',
          },
          'Site Impressions': {
            value: abbreviateLargeValue(impressions),
            sign: 1,
            displayName: 'Impressions',
          },
          'Site Endpoint Requests': {
            value: abbreviateLargeValue(endpointRequests),
            sign: 1,
            displayName: 'Endpoint Requests',
          },
          'Site Render Rate': {
            value: createLargePercentageString(renderRate),
            sign: 1,
            displayName: 'Render Rate',
          },
          'Site Bid Response Rate': {
            value: createSmallPercentageString(bidResponseRate),
            sign: 1,
            displayName: 'Bid Response Rate',
          },
        };
      } else if (this.props.resourceType === 'Stream') {
        liveDataKPIsDisplay = {
          'Stream Impressions': {
            value: abbreviateLargeValue(streamAdImpression),
            sign: 1,
            displayName: 'Impressions',
          },
          'Stream Render Rate': {
            value: createLargePercentageString(streamRenderRate),
            sign: 1,
            displayName: 'Render Rate',
          },
          'Stream Complete Rate': {
            value: createLargePercentageString(streamCompleteRate),
            sign: 1,
            displayName: 'Complete Rate',
          },
          'Stream Miss Rate': {
            value: createSmallPercentageString(streamMissRate),
            sign: 1,
            displayName: 'Miss Rate',
          },
        };
      } else if (this.props.resourceType === 'Campaign') {
        liveDataKPIsDisplay = {
          'Adserver Requests': {
            value: abbreviateLargeValue(adserverRequest),
            sign: 1,
            displayName: 'Paced Requests',
          },
          'Adserver Inventory Requests': {
            value: abbreviateLargeValue(adserverInventoryRequest),
            sign: 1,
            displayName: 'Raw Requests',
          },
          'Adserver Impressions': {
            value: abbreviateLargeValue(adserverImpression),
            sign: 1,
            displayName: 'Impressions',
          },
          'Adserver Completes': {
            value: abbreviateLargeValue(adserverComplete),
            sign: 1,
            displayName: 'Completes',
          },
        };
        if (this.props.userInfo !== undefined && hasRoleSuperUser(this.props.userInfo)) {
          liveDataKPIsDisplay['Adserver Impressions Test'] = {
            value: abbreviateLargeValue(adserverImpressionTest),
            sign: 1,
            displayName: 'Impressions With Waterfall',
          };

          liveDataKPIsDisplay['Adserver Paced Request Test'] = {
            value: abbreviateLargeValue(adserverPacedRequestTest),
            sign: 1,
            displayName: 'Paced Requests Waterfall',
          };
          liveDataKPIsDisplay['Adserver Ideal Impressions'] = {
            value: abbreviateLargeValue(adserverIdealImpressionCount),
            sign: 1,
            displayName: 'Adserver Ideal Impression Count',
          };
        }
      } else if (this.props.resourceType === 'Bidder') {
        liveDataKPIsDisplay = {
          'Bidder Revenue': {
            value: this.state.currencySymbol + abbreviateLargeValue(revenue),
            sign: 1,
            displayName: 'Revenue',
          },
          'Bidder Bid Requests': {
            value: abbreviateLargeValue(bidRequests),
            sign: 1,
            displayName: 'Bid Requests',
          },
          'Bidder eCPM': {
            value:
              this.state.currencySymbol +
              (impressions === 0 ? 0 : (revenue / impressions) * 1000).toFixed(2),
            sign: 1,
            displayName: 'eCPM',
          },
          'Bidder Impressions': {
            value: abbreviateLargeValue(impressions),
            sign: 1,
            displayName: 'Impressions',
          },
          'Bidder Render Rate': {
            value: createLargePercentageString(renderRate),
            sign: 1,
            displayName: 'Render Rate',
          },
          'Bidder Bid Response Rate': {
            value: createSmallPercentageString(bidResponseRate),
            sign: 1,
            displayName: 'Bid Response Rate',
          },
        };
      } else if (this.props.resourceType === 'Deal') {
        liveDataKPIsDisplay = {
          'Deal Revenue': {
            value: this.state.currencySymbol + abbreviateLargeValue(revenue),
            sign: 1,
            displayName: 'Revenue',
          },
          'Deal Bid Requests': {
            value: abbreviateLargeValue(bidRequests),
            sign: 1,
            displayName: 'Bid Requests',
          },
          'Deal eCPM': {
            value:
              this.state.currencySymbol +
              (impressions === 0 ? 0 : (revenue / impressions) * 1000).toFixed(2),
            sign: 1,
            displayName: 'eCPM',
          },
          'Deal Impressions': {
            value: abbreviateLargeValue(impressions),
            sign: 1,
            displayName: 'Impressions',
          },
          'Deal Render Rate': {
            value: createLargePercentageString(renderRate),
            sign: 1,
            displayName: 'Render Rate',
          },
          'Deal Bid Response Rate': {
            value: createSmallPercentageString(bidResponseRate),
            sign: 1,
            displayName: 'Bid Response Rate',
          },
        };
      } else if (this.props.resourceType === 'InventoryRoute') {
        liveDataKPIsDisplay = {
          'Inventory Route Requests': {
            value: abbreviateLargeValue(inventoryRouteRequests),
            sign: 1,
            displayName: 'Route Requests',
          },
          'Inventory Route Responses': {
            value: abbreviateLargeValue(inventoryRouteResponses),
            sign: 1,
            displayName: 'Route Responses',
          },
          'Inventory Route Errors': {
            value: abbreviateLargeValue(inventoryRouteErrors),
            sign: 1,
            displayName: 'Route Errors',
          },
          'Inventory Route No Fills': {
            value: abbreviateLargeValue(inventoryRouteNoFills),
            sign: 1,
            displayName: 'Route No Fills',
          },
          'Inventory Route Latency': {
            value: `${abbreviateLargeValue(inventoryRouteEndpointLatencyRate)}ms`,
            sign: 1,
            displayName: 'Route Latency',
          },
        };
      }
      return liveDataKPIsDisplay;
    };

    // calculates the various rates and other dependent metrics for real time analytics
    // if you need to add new metrics for the graph that involve some sort of calculation, add here
    calculateDependentMetrics = (
      realtimeAnalytics: IRealtimeAnalytics[],
      ids: string[],
    ): IRealtimeAnalytics[] => {
      const checkForNan = [
        'Revenue',
        'CPM',
        'CPMUnpaid',
        'AuctionLatency',
        'EndpointLatency',
        'BidderLatency',
        'CPMPerSecond',
        'DestinationLatency',
        'InventoryRouteEndpointLatency',
      ];
      // calculate revenue, render rate, bid response rate and average the latencies
      // if 4 hour by min (density range 240), CPM needs to be divided by 4 since each period is 4 minutes
      for (const analytic of realtimeAnalytics) {
        // remove unpaid metrics
        const cpmRawMinusUnpaid =
          analytic.CPMRaw === undefined ? 0 : analytic.CPMRaw - analytic.CPMUnpaid || 0;
        const impressionsMinusUnpaid = analytic.Impressions - analytic.ImpressionsUnpaid || 0;
        const impressionAdDurationMinusUnpaid =
          analytic.ImpressionAdDuration - analytic.ImpressionAdDurationUnpaid || 0;
        // round revenue and cpm to two places since really small decimals show up on the graph and make it looks like there's an error
        analytic.Revenue = Math.round(((analytic.CPM - analytic.CPMUnpaid) / 100000) * 100) / 100;
        analytic.CPM =
          Math.round(
            ((analytic.CPM - analytic.CPMUnpaid) /
              (impressionsMinusUnpaid *
                100 *
                (this.state.densityRange === DensityRange4HoursByMinutes ? 4 : 1))) *
              100,
          ) / 100;
        analytic.RenderRate =
          analytic.WinningBids > 0 ? (analytic.Impressions / analytic.WinningBids) * 100 : 0;
        analytic.AuctionLatency = analytic.AuctionLatency / analytic.AuctionEnd;
        analytic.BidderLatency = analytic.BidderLatency / analytic.BidResponses;
        analytic.EndpointLatency = analytic.EndpointLatency / analytic.EndpointResponses;
        analytic.CPMPerSecond =
          impressionAdDurationMinusUnpaid > 0 && cpmRawMinusUnpaid > 0
            ? cpmRawMinusUnpaid / 100 / impressionAdDurationMinusUnpaid
            : 0;
        analytic.InventoryRouteEndpointLatency = analytic.InventoryRouteEndpointLatency;
        // destinations analytics are a combo of the field name and the id and have to be specifically keyed in to calc
        for (const id of ids) {
          if (analytic['DestinationLatency' + id] && analytic['DestinationResponses' + id]) {
            analytic['DestinationLatency' + id] =
              (analytic['DestinationLatency' + id] as number) /
              (analytic['DestinationResponses' + id] as number);
          }
        }
        // Replace NaN with 0
        for (const k of checkForNan) {
          // @ts-ignore
          if (isNaN(analytic[k])) {
            // @ts-ignore
            analytic[k] = 0;
          }
        }
      }
      return realtimeAnalytics;
    };

    calculateAutomatedBidFloorDependentMetrics = (
      realtimeAnalytics: IRealtimeAnalytics[],
    ): IRealtimeAnalytics[] => {
      for (const analytic of realtimeAnalytics) {
        // BidFloor is stored on realtime as a sum of values just like any other metrics, hence
        // why we need to divide it by the number of requests to get the actual bid floor
        // We devide by 100 here cause realtime can't store floats, ex: 4.25 is stored as 425
        analytic.BidFloorOptimized =
          analytic.BidRequestsOptimized > 0
            ? analytic.BidFloorOptimized / (analytic.BidRequestsOptimized * 100)
            : 0;
        analytic.BidFloorLow =
          analytic.BidRequestsLow > 0 ? analytic.BidFloorLow / (analytic.BidRequestsLow * 100) : 0;
        analytic.BidFloorHigh =
          analytic.BidRequestsHigh > 0
            ? analytic.BidFloorHigh / (analytic.BidRequestsHigh * 100)
            : 0;

        const totalBidRequests =
          analytic.BidRequestsOptimized + analytic.BidRequestsHigh + analytic.BidRequestsLow;

        // only normalize estimated revenue
        const normalizedCPMOptimized = this.normalizeAutomatedBidFloorMetric(
          analytic.CPMOptimized,
          analytic.BidRequestsOptimized,
          totalBidRequests,
        );
        const normalizedCPMLow = this.normalizeAutomatedBidFloorMetric(
          analytic.CPMLow,
          analytic.BidRequestsLow,
          totalBidRequests,
        );
        const normalizedCPMHigh = this.normalizeAutomatedBidFloorMetric(
          analytic.CPMHigh,
          analytic.BidRequestsHigh,
          totalBidRequests,
        );
        analytic.EstimatedRevenueOptimized = this.generateEstimatedRevenue(normalizedCPMOptimized);
        analytic.EstimatedRevenueLow = this.generateEstimatedRevenue(normalizedCPMLow);
        analytic.EstimatedRevenueHigh = this.generateEstimatedRevenue(normalizedCPMHigh);

        analytic.CPMOptimized =
          analytic.WinningBidsOptimized > 0
            ? analytic.CPMOptimized / (analytic.WinningBidsOptimized * 100)
            : analytic.CPMOptimized;
        analytic.CPMLow =
          analytic.WinningBidsLow > 0
            ? analytic.CPMLow / (analytic.WinningBidsLow * 100)
            : analytic.CPMLow;
        analytic.CPMHigh =
          analytic.WinningBidsHigh > 0
            ? analytic.CPMHigh / (analytic.WinningBidsHigh * 100)
            : analytic.CPMHigh;
      }
      return realtimeAnalytics;
    };

    // normalize the bid won cpm based on the ratio between the bid requests directed
    // to this bid floor and the total bid requests across all bid floors
    normalizeAutomatedBidFloorMetric = (
      metric: number,
      bidRequests: number,
      totalBidRequests: number,
    ): number => {
      if (metric === 0 || bidRequests === 0 || totalBidRequests === 0) return 0;
      return Math.round((metric / bidRequests) * totalBidRequests);
    };

    generateEstimatedRevenue = (CPM: number): number => {
      return CPM > 0 ? CPM / 100000 : 0;
    };

    // adds refresh data and any data needing to be regenned to current data set
    // also removes any old data
    addRefreshData = (
      oldData: IRealtimeAnalytics[],
      newData: IRealtimeAnalytics[],
    ): IRealtimeAnalytics[] => {
      // if we have a full range of data for the range, remove the oldest item(s)
      let maxLength = this.state.densityRange;
      // shift off last data point if density range 15, 30, 60 or 240
      if (maxLength !== 4 && maxLength !== 12 && maxLength !== 24) {
        while (oldData.length >= maxLength) {
          oldData.shift();
        }
      }

      // since the refresh checks the last 5 minutes, we need to find and replace
      // any minutes from the old data with new data
      for (const newDatum of newData) {
        let matchFound = false;
        for (let i = 0; i < oldData.length; i++) {
          if (oldData[i].Date === newDatum.Date) {
            matchFound = true;
            oldData[i] = newDatum;
            break;
          }
        }
        // if no match is found, the new data will be placed at the end
        if (!matchFound) {
          oldData.push(newDatum);
        }
      }

      // if 4 or 12 or 24 hours by hour, replace the last hour first, then push out an old hour if limit reached
      if (maxLength === 4 || maxLength === 12 || maxLength === 24) {
        // Automated Bid Floors are 24 half hour units representing the last 12 hours
        if (this.props.resourceType === 'AutomatedBidFloor') {
          maxLength = 24;
        }

        while (oldData.length > maxLength) {
          oldData.shift();
        }
      }
      return oldData;
    };

    // combines raw data (minute by minute blocks) into the desired time periods
    // for example, 4 hours uses 4 minute blocks
    combineData = (rawData: IRealtimeAnalytics[], densityRange: number): IRealtimeAnalytics[] => {
      const combinedData = [] as IRealtimeAnalytics[];
      // combine raw data into 4 minute periods
      const periodDuration = 4;
      const numPeriods = Math.ceil(rawData.length / periodDuration);
      for (let period = 0; period < numPeriods; period++) {
        // create new aggregator
        const combinedDatum = createNewAggregator(0, '', densityRange);
        // loop over each minute for a period
        for (let i = 0; i < periodDuration; i++) {
          const minute = period * periodDuration + i;
          // skip if end is reached
          if (minute >= rawData.length) {
            continue;
          }
          const rawDatum = rawData[minute] as IRealtimeAnalytics;
          // if first index, take that date as date for the period
          if (i === 0) {
            combinedDatum.Date = rawDatum.Date;
            combinedDatum.DateTooltip = rawDatum.DateTooltip;
          }
          // loop over metrics
          for (const metric of Object.keys(rawDatum)) {
            const key = metric as GraphMetricKeys;
            if (metric === 'Date' || metric === 'DateTooltip') {
              continue;
            }
            if (!combinedDatum[key]) {
              combinedDatum[key] = 0;
            }
            combinedDatum[key] += (rawDatum[key] || 0) as number;
          }
        }
        // at the end of the period, average the latencies and render rate
        const averageMetrics = ['RenderRate', 'BidderLatency', 'AuctionLatency', 'EndpointLatency'];
        for (let i = 0; i < averageMetrics.length; i++) {
          const key = averageMetrics[i] as GraphMetricKeys;
          if (key in combinedDatum) {
            combinedDatum[key] = ((combinedDatum[key] || 0) as number) / periodDuration;
          }
        }
        combinedData.push(combinedDatum);
      }
      return combinedData;
    };

    // handler helper functions
    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          [target.name]: target.value,
        },
        () => {
          if (target.name === 'densityRange') {
            this.trackEvent('Live Data - Change Live Data Range');
          }
          this.loadDensityData();
        },
      );
    };

    handleChangeFilterABTestingName = (activeABTestName: string, status: boolean) => {
      const { resourceType } = this.props;
      if (resourceType === 'Bidder') {
        this.setState(
          {
            currentBidderABTestingName: activeABTestName,
            filterBidderABTestingName: status,
          },
          this.loadDensityData,
        );
      } else if (resourceType === 'Site') {
        this.setState(
          {
            currentChannelABTestingName: activeABTestName,
            filterChannelABTestingName: status,
          },
          this.loadDensityData,
        );
      }
    };

    // the ID is the color code, description is the key and title is what is displayed
    handleMetricsAcceptClick = (values: IMenuList): void => {
      const { resourceType } = this.props;
      const selectedMetrics = {} as ISelectedMetrics;
      const doNotDisplayTitles = [] as string[];
      Object.values(values).forEach((listItem: IListItem) => {
        if (listItem.selected) {
          // StreamStart and Chunklist response are not displayed if inventory splits are chosen
          if (
            (listItem.title === 'Stream Starts' || listItem.title === 'Chunklist Resp') &&
            this.state.selectedIDs.length > 0 &&
            this.props.resourceType === 'Stream'
          ) {
            doNotDisplayTitles.push(listItem.title);
          }
          let metricKey = listItem.description as GraphMetricKeys;
          if (resourceType === 'AutomatedBidFloor') {
            (selectedMetrics.BidFloorOptimized = {
              color: graphColors.lightOrange,
              graphType: 'Line',
              yAxisId: 'small',
            }),
              (metricKey = (listItem.description + 'Low') as GraphMetricKeys);
            selectedMetrics[metricKey] = {
              color: graphColors.cyan,
              graphType: listItem.graphType || 'Line',
              yAxisId: listItem.yAxisId || 'big',
            };
            metricKey = (listItem.description + 'Optimized') as GraphMetricKeys;
            selectedMetrics[metricKey] = {
              color: graphColors.lightBlue,
              graphType: listItem.graphType || 'Line',
              yAxisId: listItem.yAxisId || 'big',
            };
            metricKey = (listItem.description + 'High') as GraphMetricKeys;
            selectedMetrics[metricKey] = {
              color: graphColors.turquoise,
              graphType: listItem.graphType || 'Line',
              yAxisId: listItem.yAxisId || 'big',
            };
          } else {
            selectedMetrics[metricKey] = {
              color: listItem.ID,
              graphType: listItem.graphType || 'Line',
              yAxisId: listItem.yAxisId || 'big',
            };
          }
        }
      });

      // setting timeout so click from menu does not dismiss this error msg
      if (doNotDisplayTitles.length > 0) {
        setTimeout(() => {
          this.setState({
            snackbarMessage: `${doNotDisplayTitles.join(
              ', ',
            )} cannot be displayed if Inventory Splits are selected.`,
          });
        }, 1000);
      }
      this.setState(
        {
          selectedMetrics,
        },
        () => {
          this.trackEvent('Live Data - Change Live Data Metrics');
          this.loadDensityData();
        },
      );
    };

    // the ID is the selected metrics key and the title is the color
    handleChangeFilter = (selectedIDs: string[], doNotDisplayTitleOptions: string[]): void => {
      const doNotDisplayTitles = [] as string[];
      // StreamStart and Chunklist response are not displayed if inventory splits are chosen
      doNotDisplayTitleOptions.forEach((title) => {
        if (title in this.state.selectedMetrics) {
          doNotDisplayTitles.push(title);
        }
      });
      if (selectedIDs.length > 0 && doNotDisplayTitles.length > 0) {
        // setting timeout so click from menu does not dismiss this error msg
        setTimeout(() => {
          this.setState({
            snackbarMessage: `${doNotDisplayTitles.join(', ')} cannot be displayed if ${
              this.state.filterTitle
            } are selected.`,
          });
        }, 1000);
      }
      this.setState(
        {
          selectedIDs,
        },
        this.loadDensityData,
      );
    };

    handleSnackbarClose = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    renderTop = () => {
      const {
        allBidderABTestingTests,
        allChannelABTestingTests,
        currentBidderABTestingName,
        currentChannelABTestingName,
        hidden,
        metricsList,
        selectedIDs,
      } = this.state;
      const { classes, resourceIDs, resourceType } = this.props;

      const isBidderResourceType = resourceType === 'Bidder';
      const abTestingTests =
        resourceType === 'Bidder' ? allBidderABTestingTests : allChannelABTestingTests;
      const isAutomatedBidFloorResourceType = resourceType === 'AutomatedBidFloor';
      const isSuperUser =
        this.props.userInfo !== undefined && hasRoleSuperUser(this.props.userInfo);
      return (
        <Cols
          horizontalPadding
          smallVerticalPadding
          gap={spacing.medium}
          style={{
            width: '100%',
            alignItems: 'center',
          }}
        >
          {!hidden && (
            <Col flex>
              <Cols
                wrap
                gap={spacing.medium}
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  rowGap: '12px',
                }}
              >
                {/* A/B Filters */}
                {!isAutomatedBidFloorResourceType &&
                  (isSuperUser || isSamsungPublisher(this.props.publisher.ID)) && (
                    <Col>
                      <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                        <Col>
                          <Text small muted tKey='ABFiltersWithAsterisk' />
                        </Col>
                        <Col>
                          <TextField
                            fullWidth
                            label=''
                            select={true}
                            name={
                              isBidderResourceType
                                ? 'currentBidderABTestingName'
                                : 'currentChannelABTestingName'
                            }
                            value={
                              isBidderResourceType
                                ? currentBidderABTestingName
                                : currentChannelABTestingName
                            }
                            margin='none'
                            size='small'
                            className={classes.input}
                            InputProps={{ classes: { root: classes.input } }}
                            style={{
                              width: 112,
                              marginBottom: 4,
                              fontSize: 14,
                            }}
                          >
                            <MenuItem
                              style={{ fontSize: 16 }}
                              value=''
                              onClick={() => this.handleChangeFilterABTestingName('', false)}
                            >
                              {ct('ClearItem', { item: ct('Filter') })}
                            </MenuItem>
                            {Object.values(abTestingTests).map((abTestingTest: IListItem) => {
                              return (
                                <MenuItem
                                  style={{ fontSize: 16 }}
                                  key={abTestingTest.title}
                                  value={abTestingTest.title || 'A'}
                                  onClick={() =>
                                    this.handleChangeFilterABTestingName(
                                      abTestingTest.title || 'A',
                                      true,
                                    )
                                  }
                                >
                                  {abTestingTest.title || 'A'}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Col>
                      </Cols>
                    </Col>
                  )}
                {/* Metrics Dropdown */}
                <Col>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col>
                      <Text small muted tKey='Metrics' />
                    </Col>
                    <Col>
                      {isAutomatedBidFloorResourceType ? (
                        <MaterialSearchDropdownMulti
                          width='128px'
                          fontSize='14px'
                          selectAll={false}
                          enableMultiSelect={false}
                          menuTitle=''
                          list={metricsList}
                          onChange={(list: IMenuList) => {
                            this.setState({
                              metricsList: list,
                            });
                          }}
                          handleMenuAcceptClick={this.handleMetricsAcceptClick}
                          maxHeight={320}
                          disableSearch={true}
                          selectLimit={1}
                          responsivePositionEnabled
                        />
                      ) : (
                        <MaterialSearchDropdownMulti
                          width='128px'
                          fontSize='14px'
                          selectAll={true}
                          enableMultiSelect={true}
                          menuTitle=''
                          list={metricsList}
                          onChange={(list: IMenuList) => {
                            this.setState({
                              metricsList: list,
                            });
                          }}
                          handleMenuAcceptClick={this.handleMetricsAcceptClick}
                          maxHeight={320}
                          disableSearch={true}
                          responsivePositionEnabled
                        />
                      )}
                    </Col>
                  </Cols>
                </Col>
                {/* Inventory Split Dropdown */}
                {this.state.includeFilter && (
                  <Col>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col>
                        <Text small muted>
                          {this.state.filterTitle}
                        </Text>
                      </Col>
                      <Col>
                        <LiveDataMultiSelect
                          onChange={(selectedIDs) =>
                            this.handleChangeFilter(
                              selectedIDs,
                              this.state.doNotDisplayTitleOptions,
                            )
                          }
                          publisherID={this.props.publisher.ID}
                          resourceType={this.props.resourceType}
                          // it is expected that only one ID is passed here
                          // resourceIDs is plural since orders can have many campaigns
                          resourceID={resourceIDs}
                          filterObjectIDs={selectedIDs}
                          menuTitle={this.state.filterMenuTitle}
                          history={this.props.history}
                          location={this.props.location}
                          userInfo={this.props.userInfo}
                        />
                      </Col>
                    </Cols>
                  </Col>
                )}
                {/* Range Dropdown */}
                {!isAutomatedBidFloorResourceType && (
                  <Col>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col>
                        <Text small muted tKey='Range' />
                      </Col>
                      <Col>
                        <TextField
                          fullWidth
                          label=''
                          select={true}
                          name='densityRange'
                          value={this.state.densityRange}
                          onChange={this.handleChange}
                          margin='none'
                          size='small'
                          className={classes.input}
                          InputProps={{ classes: { root: classes.input } }}
                          style={{
                            width: 150,
                            marginBottom: 4,
                            fontSize: 14,
                          }}
                        >
                          {[
                            { label: ct('FifteenMinutes'), value: DensityRange15Minutes },
                            { label: ct('ThirtyMinutes'), value: DensityRange30Minutes },
                            { label: ct('OneHourByMinute'), value: DensityRange1HourByMinutes },
                            { label: ct('FourHoursByMinute'), value: DensityRange4HoursByMinutes },
                            { label: ct('FourHoursByHour'), value: DensityRange4HoursByHour },
                            { label: ct('TwelveHoursByHour'), value: DensityRange12HoursByHour },
                            {
                              label: ct('TwentyFourHoursByHour'),
                              value: DensityRange24HoursByHour,
                            },
                            { label: ct('WeekByHour'), value: DensityRangeWeekByHour },
                          ].map(({ label, value }) => (
                            <MenuItem style={{ fontSize: 16 }} key={value} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Col>
                    </Cols>
                  </Col>
                )}
              </Cols>
            </Col>
          )}
        </Cols>
      );
    };

    renderGraph = () => {
      const { data1, loading1, selectedMetrics, selectedIDs, currencySymbol } = this.state;
      const { classes } = this.props;
      return (
        <Grid item className={classes.liveDataItem}>
          {/* Metrics graph */}
          <LiveDataGraph
            classes={classes}
            data1={data1}
            loaded={loading1}
            LoadingStateHasData={LoadingStateHasData}
            selectedMetrics={selectedMetrics}
            stackedBarMetrics={this.state.stackedBarMetrics}
            selectedIDs={selectedIDs}
            graphTitle=''
            currencySymbol={currencySymbol}
            resourceType={this.props.resourceType}
            publisherID={this.props.publisher.ID}
            location={this.props.location}
            history={this.props.history}
          />
        </Grid>
      );
    };

    renderKPIs = () => {
      const { liveDataKPIsDisplay, selectedIDs } = this.state;
      const { classes, resourceType } = this.props;
      return (
        <Grid item className={classes.liveDataItem}>
          {/* KPIs */}
          <LiveDataKPIs
            entries={liveDataKPIsDisplay}
            classes={classes}
            inventorySplitActive={selectedIDs.length > 0 && resourceType === 'Stream'}
          />
        </Grid>
      );
    };

    render = () => {
      const { hidden, snackbarError, snackbarMessage } = this.state;
      const { maxWidth } = this.props;
      return (
        <Grid
          container
          style={{
            maxWidth: maxWidth ? maxWidth : '100%',
          }}
          direction='column'
          alignItems='center'
        >
          {this.renderTop()}
          {!hidden && this.renderKPIs()}
          {!hidden && this.renderGraph()}
          <SnackbarView
            open={!!snackbarMessage}
            error={!!snackbarError}
            onClose={this.handleSnackbarClose}
          >
            {snackbarMessage}
          </SnackbarView>
        </Grid>
      );
    };
  },
);
